import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import BAULT_ABI from 'abis/Bault.json'
import BAULT_ROUTER_ABI from 'abis/BaultRouter.json'
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import REWARD_VAULT_ABI from 'abis/RewardVault.json'
import { useStepLineState } from 'components/StepLine/context/StepLineContext'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import { useCallback, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { Address } from 'viem'

import { SelectLockTimeState } from '../steps/SelectLockTimeStep'

export const useOnStake = (
  stakeAmount: CurrencyAmount<Token> | undefined,
  farmAddress: Address | undefined,
  clear?: () => void
) => {
  const { lockTime } = useStepLineState<SelectLockTimeState>()
  const { writeContractAsync } = useWriteContract()
  const { wagmiPublicClient, account } = useConnection()
  const [attemptingTxnStake, setAttemptingTxnStake] = useState(false)
  const pendingTextStake = 'Stake'
  const [txHashStake, setTxHashStake] = useState<string>('')
  const addTransaction = useTransactionAdder()

  const onStake = useCallback(
    async function onStake() {
      if (lockTime === undefined || !stakeAmount || !farmAddress || !wagmiPublicClient) {
        return
      }

      setAttemptingTxnStake(true)

      try {
        const txn = {
          address: farmAddress,
          abi: KODIAK_FARM_ABI,
          functionName: 'stakeLocked',
          args: [BigInt(stakeAmount.quotient.toString()), BigInt(lockTime)],
          account,
        }

        const gas = await wagmiPublicClient.estimateContractGas(txn)

        const hash = await writeContractAsync({
          ...txn,
          gas: calculateGasMargin(gas as bigint) as bigint,
        })

        setTxHashStake(hash)

        addTransaction(
          { hash },
          {
            islandId: stakeAmount.currency.address,
            lockTime: lockTime.toString(),
            stakeAmount: stakeAmount.quotient.toString(),
            type: TransactionType.STAKE_LIQUIDITY_ISLAND,
          }
        )

        clear && clear()
      } catch (e) {
        console.error(e)
      } finally {
        setAttemptingTxnStake(false)
      }
    },
    [
      attemptingTxnStake,
      pendingTextStake,
      txHashStake,
      addTransaction,
      stakeAmount,
      lockTime,
      farmAddress,
      writeContractAsync,
      clear,
      wagmiPublicClient,
      account,
    ]
  )

  return {
    onStake,
    attemptingTxnStake,
    pendingTextStake,
    txHashStake,
    clear: () => {
      setAttemptingTxnStake(false)
      setTxHashStake('')
      clear && clear()
    },
  }
}

/**
 * Hook for handling vault staking (without lock time)
 */
export const useOnVaultStake = (
  stakeAmount: CurrencyAmount<Token> | undefined,
  vaultAddress: Address | undefined,
  clear?: () => void
) => {
  const { writeContractAsync } = useWriteContract()
  const { wagmiPublicClient, account } = useConnection()
  const [attemptingTxnStake, setAttemptingTxnStake] = useState(false)
  const pendingTextStake = 'Stake'
  const [txHashStake, setTxHashStake] = useState<string>('')
  const addTransaction = useTransactionAdder()

  const onStake = useCallback(
    async function onStake() {
      if (!stakeAmount || !vaultAddress || !wagmiPublicClient) {
        return
      }

      setAttemptingTxnStake(true)

      try {
        const txn = {
          address: vaultAddress,
          abi: REWARD_VAULT_ABI,
          functionName: 'stake',
          args: [BigInt(stakeAmount.quotient.toString())],
          account,
        }
        const gas = await wagmiPublicClient.estimateContractGas(txn)
        const hash = await writeContractAsync({
          ...txn,
          gas: calculateGasMargin(gas as bigint) as bigint,
        })

        setTxHashStake(hash)

        addTransaction(
          { hash },
          {
            islandId: stakeAmount.currency.address,
            lockTime: '0',
            stakeAmount: stakeAmount.quotient.toString(),
            type: TransactionType.STAKE_LIQUIDITY_ISLAND,
          }
        )

        clear && clear()
      } catch (e) {
        console.error(e)
      } finally {
        setAttemptingTxnStake(false)
      }
    },
    [
      attemptingTxnStake,
      pendingTextStake,
      txHashStake,
      addTransaction,
      stakeAmount,
      vaultAddress,
      writeContractAsync,
      clear,
      wagmiPublicClient,
      account,
    ]
  )

  return {
    onStake,
    attemptingTxnStake,
    pendingTextStake,
    txHashStake,
    clear: () => {
      setAttemptingTxnStake(false)
      setTxHashStake('')
      clear && clear()
    },
  }
}

export const useOnBaultDeposit = (
  depositAmount: CurrencyAmount<Token> | undefined,
  baultRouterAddress: Address | undefined,
  baultAddress: Address | undefined,
  userAddress: Address | undefined,
  clear?: () => void
) => {
  const { writeContractAsync } = useWriteContract()
  const [attemptingTxnStake, setAttemptingTxnStake] = useState(false)
  const pendingTextStake = 'Deposit'
  const [txHashStake, setTxHashStake] = useState<string>('')
  const addTransaction = useTransactionAdder()
  const { wagmiPublicClient } = useConnection()

  const onStake = useCallback(
    async function onStake() {
      if (!depositAmount || !baultRouterAddress || !baultAddress || !wagmiPublicClient) {
        return
      }

      setAttemptingTxnStake(true)

      try {
        // For subsequent deposits, use normal slippage tolerance
        const expectedShares = await wagmiPublicClient.readContract({
          address: baultAddress,
          abi: BAULT_ABI,
          functionName: 'previewDeposit',
          args: [BigInt(depositAmount.quotient.toString())],
        })

        const minSharesOut = (BigInt(expectedShares as string) * 990n) / 1000n // 1% slippage

        const txn = {
          address: baultRouterAddress,
          abi: BAULT_ROUTER_ABI,
          functionName: 'depositIntoBault',
          args: [baultAddress, BigInt(depositAmount.quotient.toString()), minSharesOut, userAddress],
          account: userAddress,
        }
        const gas = await wagmiPublicClient.estimateContractGas(txn)

        // Execute transaction
        const hash = await writeContractAsync({
          ...txn,
          gas: calculateGasMargin(gas as bigint),
        })

        setTxHashStake(hash)

        // Add to transaction state
        addTransaction(
          { hash },
          {
            poolId: depositAmount.currency.address,
            amount: `${depositAmount.toSignificant(6)} ${depositAmount.currency.symbol}`,
            baultAddress,
            type: TransactionType.DEPOSIT_INTO_BAULT,
          }
        )

        clear && clear()
      } catch (e) {
        console.error(e)
      } finally {
        setAttemptingTxnStake(false)
      }
    },
    [
      depositAmount,
      baultRouterAddress,
      baultAddress,
      userAddress,
      addTransaction,
      clear,
      writeContractAsync,
      wagmiPublicClient,
    ]
  )

  return {
    onStake,
    attemptingTxnStake,
    pendingTextStake,
    txHashStake,
    clear: () => {
      setAttemptingTxnStake(false)
      setTxHashStake('')
      clear && clear()
    },
  }
}
